// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { first, last } from 'lodash';
import glamorous, { Span } from 'glamorous';
import t from 'app/utils/translate';
import Responsive from 'app/components/responsive/Responsive';
import Grid from 'app/components/layout/Grid';
import GridCol from 'app/components/layout/GridCol';
import Loader from 'app/components/commons/Loader';
import { fetchUltimateChallenge } from 'app/actions/globalLeaderboardActions';
import Tappable from 'app/components/commons/Tappable';
import MyScoreRow from './MyScoreRow';
import WinnersPodium from './WinnersPodium';
import LeaderboardHeader from './LeaderboardHeader';
import LeaderboardList from './LeaderboardList';
import MyScoreBlock from './MyScoreBlock';

type Props = {
  competitionId: string,
  displayCountry: boolean,
  entries: Array<Object>,
  errorStatus: ?number,
  hasEntered: boolean,
  hasCustomBanner: boolean,
  isLoading: boolean,
  isSignedIn: boolean,
  isVerified?: boolean,
  onFetch: (?string) => void,
  spartan: boolean,
  updatedTime: string,
};

const StyledLoader = glamorous(Loader)(props => ({
  paddingTop: props.theme.spacing.large,
}));

const ComingSoon = glamorous.div(props => ({
  paddingTop: props.theme.spacing.large,
  textAlign: 'center',
  width: '100%',
  fontSize: props.theme.fontSize.default,
  color: props.theme.color.white,
}));

const Warning = glamorous.div(props => ({
  backgroundColor: props.theme.color.warning,
  textTransform: 'uppercase',
  padding: props.theme.spacing.small,
  fontSize: props.theme.fontSize.default,
  color: props.theme.color.basis6,
}));

const HyperLink = glamorous(Tappable)(props => ({
  color: props.theme.color.white,
  paddingLeft: 5,
}));

class UltimateChampionContent extends Component<Props> {
  props: Props;

  static defaultProps = {
    isVerified: false,
  };

  componentDidMount() {
    this.props.onFetch(this.props.competitionId);
  }

  componentDidUpdate(prevProps: Props) {
    const { competitionId, isSignedIn } = this.props;

    if ((isSignedIn && !prevProps.isSignedIn) || competitionId !== prevProps.competitionId) {
      this.props.onFetch(competitionId);
    }
  }

  render() {
    const {
      displayCountry,
      entries,
      errorStatus,
      hasEntered,
      hasCustomBanner,
      isLoading,
      isSignedIn,
      isVerified,
      updatedTime,
      spartan,
    } = this.props;

    if (isLoading) {
      return <StyledLoader />;
    }
    if (errorStatus === 404) {
      return <ComingSoon>{t('Ultimate Champion Competition Coming Soon!')}</ComingSoon>;
    }

    return (
      <div>
        {hasEntered && (
          <Responsive mobile>
            <MyScoreRow />
          </Responsive>
        )}
        {!hasCustomBanner && <WinnersPodium displayCountry={displayCountry} entries={entries} />}
        {isSignedIn &&
          !isVerified && (
            <Warning>
              <Span fontSize="1.2em" paddingRight={4} className="ion-android-warning" />
              {t('Your account must be verified to appear on the leaderboard.')}
              <HyperLink component={Link} to="/account/profile">
                {t('Verify now')}
                <Span paddingLeft={4} className="ion-android-open" />
              </HyperLink>
            </Warning>
          )}
        <Grid>
          <GridCol width={hasEntered ? 'two-thirds' : undefined}>
            <LeaderboardHeader updatedTime={updatedTime} />
            {entries.length === 0 ? (
              <div className="text-center">Come back soon to see the season leaderboard!</div>
            ) : (
              <LeaderboardList
                displayCountry={displayCountry}
                entries={entries}
                highestScore={first(entries).globalScore}
                lowScore={last(entries).globalScore}
                spartan={spartan}
              />
            )}
          </GridCol>
          <Responsive desktop={hasEntered}>
            <GridCol width="one-third">
              <MyScoreBlock />
            </GridCol>
          </Responsive>
        </Grid>
      </div>
    );
  }
}

function mapStateToProps(state: GlobalState) {
  return {
    displayCountry: state.settings.displayCountry,
    entries: state.ultimateChallenge.entries,
    errorStatus: state.ultimateChallenge.errorStatus,
    hasEntered: state.ultimateChallenge.entered,
    isLoading: state.ultimateChallenge.isLoading,
    isSignedIn: state.user.isSignedIn,
    isVerified: state.user.currentUser.id_verified,
    updatedTime: state.ultimateChallenge.updatedTime,
  };
}

const actions = {
  onFetch: fetchUltimateChallenge,
};

export default connect(mapStateToProps, actions)(UltimateChampionContent);
